import React, { useEffect, useState } from 'react';
import { MagnifyingGlassIcon, CheckIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';
import jobService from '../../services/jobService';

/**
 * StepProductSelection Component
 * Second step of job creation wizard - multi-select products with search
 */
export function StepProductSelection({ formData, updateFormData, onNext, onBack }) {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [selectedProductIds, setSelectedProductIds] = useState(formData.productIds || []);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [validationError, setValidationError] = useState('');

  // Load products on mount
  useEffect(() => {
    const loadProducts = async () => {
      setIsLoading(true);
      setLoadError('');

      try {
        const productsData = await jobService.getProducts();
        setProducts(productsData);
        setFilteredProducts(productsData);
      } catch (error) {
        console.error('Failed to load products:', error);
        setLoadError('Erro ao carregar produtos. Tente novamente.');
      } finally {
        setIsLoading(false);
      }
    };

    loadProducts();
  }, []);

  // Filter products based on search query
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredProducts(products);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filtered = products.filter(
      (product) =>
        product.nome.toLowerCase().includes(query) ||
        product.codigo.toLowerCase().includes(query) ||
        (product.categoria && product.categoria.toLowerCase().includes(query))
    );

    setFilteredProducts(filtered);
  }, [searchQuery, products]);

  const toggleProduct = (productId) => {
    setValidationError('');

    if (selectedProductIds.includes(productId)) {
      setSelectedProductIds(selectedProductIds.filter((id) => id !== productId));
    } else {
      // Limit to 500 products max
      if (selectedProductIds.length >= 500) {
        setValidationError('Máximo de 500 produtos por job. Considere criar múltiplos jobs.');
        return;
      }
      setSelectedProductIds([...selectedProductIds, productId]);
    }
  };

  const toggleSelectAll = () => {
    setValidationError('');

    if (selectedProductIds.length === filteredProducts.length) {
      // Deselect all filtered products
      const filteredIds = filteredProducts.map((p) => p.id);
      setSelectedProductIds(selectedProductIds.filter((id) => !filteredIds.includes(id)));
    } else {
      // Select all filtered products (up to limit)
      const filteredIds = filteredProducts.map((p) => p.id);
      const newSelected = [...new Set([...selectedProductIds, ...filteredIds])];

      if (newSelected.length > 500) {
        setValidationError('Máximo de 500 produtos por job. Considere criar múltiplos jobs.');
        return;
      }

      setSelectedProductIds(newSelected);
    }
  };

  const handleNext = () => {
    if (selectedProductIds.length === 0) {
      setValidationError('Selecione ao menos um produto');
      return;
    }

    updateFormData({ productIds: selectedProductIds });
    onNext();
  };

  const handleBack = () => {
    updateFormData({ productIds: selectedProductIds });
    onBack();
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Carregando produtos...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (loadError) {
    return (
      <div className="py-12">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <ExclamationCircleIcon className="w-12 h-12 text-red-600 mx-auto mb-3" />
          <p className="text-red-800 font-medium mb-2">{loadError}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            Tentar Novamente
          </button>
        </div>
      </div>
    );
  }

  const allFilteredSelected =
    filteredProducts.length > 0 &&
    filteredProducts.every((p) => selectedProductIds.includes(p.id));

  return (
    <div className="space-y-6">
      {/* Selection Counter */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-semibold text-blue-900">
              {selectedProductIds.length} produto{selectedProductIds.length !== 1 ? 's' : ''} selecionado{selectedProductIds.length !== 1 ? 's' : ''}
            </p>
            <p className="text-xs text-blue-700 mt-0.5">
              Máximo: 500 produtos por job
            </p>
          </div>
          {selectedProductIds.length > 0 && (
            <button
              onClick={() => setSelectedProductIds([])}
              className="text-sm text-blue-700 hover:text-blue-900 font-medium underline"
            >
              Limpar Seleção
            </button>
          )}
        </div>
      </div>

      {/* Validation Error */}
      {validationError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <ExclamationCircleIcon className="w-5 h-5 text-red-600 flex-shrink-0" />
            <p className="text-sm text-red-800 font-medium">{validationError}</p>
          </div>
        </div>
      )}

      {/* Search Bar */}
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
          <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
        </div>
        <input
          type="text"
          placeholder="Buscar por nome, código ou categoria..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-11 pr-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition-all"
        />
        {searchQuery && (
          <button
            onClick={() => setSearchQuery('')}
            className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-gray-600"
          >
            
          </button>
        )}
      </div>

      {/* Select All Checkbox */}
      {filteredProducts.length > 0 && (
        <div className="flex items-center gap-3 py-2">
          <input
            type="checkbox"
            id="select-all"
            checked={allFilteredSelected}
            onChange={toggleSelectAll}
            className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 focus:ring-2 cursor-pointer"
          />
          <label htmlFor="select-all" className="text-sm font-semibold text-gray-700 cursor-pointer">
            Selecionar todos ({filteredProducts.length})
          </label>
        </div>
      )}

      {/* Products List */}
      <div className="border-2 border-gray-200 rounded-lg overflow-hidden">
        {filteredProducts.length === 0 ? (
          <div className="p-8 text-center">
            <MagnifyingGlassIcon className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p className="text-gray-500 font-medium">
              {searchQuery
                ? `Nenhum produto encontrado com "${searchQuery}"`
                : 'Nenhum produto disponível'}
            </p>
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="mt-3 text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                Limpar busca
              </button>
            )}
          </div>
        ) : (
          <div className="max-h-96 overflow-y-auto">
            {filteredProducts.map((product) => {
              const isSelected = selectedProductIds.includes(product.id);

              return (
                <label
                  key={product.id}
                  className={`flex items-center gap-4 p-4 border-b border-gray-200 last:border-0 cursor-pointer transition-colors ${
                    isSelected ? 'bg-blue-50 hover:bg-blue-100' : 'hover:bg-gray-50'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggleProduct(product.id)}
                    className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 focus:ring-2 cursor-pointer"
                  />

                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <p className="font-semibold text-gray-900 truncate">
                          {product.nome}
                        </p>
                        <div className="flex items-center gap-3 mt-1">
                          <span className="text-xs font-medium text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                            {product.codigo}
                          </span>
                          {product.categoria && (
                            <span className="text-xs text-gray-600">
                              {product.categoria}
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="text-right flex-shrink-0">
                        <p className="text-sm font-bold text-gray-900">
                          R$ {product.preco_unitario.toFixed(2)}
                        </p>
                        <p className="text-xs text-gray-500">{product.unidade}</p>
                      </div>
                    </div>
                  </div>

                  {isSelected && (
                    <CheckIcon className="w-5 h-5 text-blue-600 flex-shrink-0" />
                  )}
                </label>
              );
            })}
          </div>
        )}
      </div>

      {/* Navigation Buttons */}
      <div className="flex items-center justify-between pt-4">
        <button
          type="button"
          onClick={handleBack}
          className="px-6 py-3 bg-white border-2 border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-4 focus:ring-gray-100 transition-all"
        >
          ← Voltar
        </button>

        <button
          type="button"
          onClick={handleNext}
          className="px-8 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold rounded-lg shadow-lg hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-4 focus:ring-blue-100 transition-all transform hover:scale-[1.02] active:scale-[0.98]"
        >
          Próximo: Revisar →
        </button>
      </div>
    </div>
  );
}
